/**
 * useDebugPanelVisible — gate hook deciding whether the DebugPanel renders.
 *
 * Visible IFF:
 *   - `user.primaryRole === 'SuperAdmin'` (always-on for admins), OR
 *   - Test Mode is enabled (`config.systemSettings.testModeEnabled === true`).
 *
 * For everyone else the panel is not rendered AND the shell grid does not
 * reserve any height for it (see ShellLayout / layout.css conditional class).
 *
 * NOTE: Test Mode write-side gating lands in a later sprint — this hook only
 * consumes whatever's already in the SystemSettings catalog, so the panel can
 * be QA'd today without the full Test Mode admin surface.
 */
import { useAuth } from '@/contexts/AuthContext';
import { useTestMode } from './useTestMode';

export function useDebugPanelVisible(): boolean {
  const { user } = useAuth();
  const { enabled: testMode } = useTestMode();
  if (!user) return false;
  // PILOT 2026-09-11 (Bill): the SuperAdmin always-on branch is GONE. It put a
  // floating "DEBUG / 50 API calls" panel over the bottom-right of every
  // screen for any admin — including during a demo or an over-the-shoulder
  // walkthrough with an FSR — and it overlapped real UI. Test Mode is now the
  // single switch: flip it on to debug, off to present. Nothing is lost, it
  // just stopped being the default.
  return testMode === true;
}
